import { SelectListEvent } from '@acies/core';
import AutoCompleteEvent from './auto-complete-event';
import type DropdownItem from './dropdown-item';
import DropdownItemRenderer from './dropdown-item-renderer';
import { ICONS } from './icons';
import InputField from './input-field';
import InputFieldEvent from './input-field-event';
import PopoverContainer from './popover-container';

export default class AutoComplete extends PopoverContainer<DropdownItem> {
	public static override observedAttributes = [...PopoverContainer.observedAttributes, 'icon', 'placeholder'];
	public set icon(value: null | string) {
		assertIsValidIconName(value);
		this.inputField.icon = value;
	}

	public get icon(): keyof typeof ICONS | null {
		return this.inputField.icon;
	}

	public set placeholder(value: string) {
		this.inputField.placeholder = value;
	}

	public get placeholder(): string {
		return this.inputField.placeholder;
	}

	#hasKeyboardListener: boolean;
	#inputField!: InputField;

	private get inputField(): InputField {
		if (!this.#inputField) {
			this.#inputField = new InputField();
			this.#inputField.width = 'fill';
		}
		return this.#inputField;
	}

	public constructor() {
		super();
		this.#hasKeyboardListener = false;
		this.arrowDownListener = this.arrowDownListener.bind(this);
		this.dataCollection.filterProperties = ['text'];
		this.dataCollection.sortFunction = this.sortFunction.bind(this);
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'icon') {
			this.#iconAttributeChanged(value);
		} else if (name === 'placeholder') {
			this.#placeholderAttributeChanged(value);
		}
	}

	protected override initialize(): void {
		this.addComponent(this.inputField);
		super.initialize();
		this.#addEventListeners();
	}

	#addArrowDownListener(): void {
		if (!this.#hasKeyboardListener) {
			this.#hasKeyboardListener = true;
			window.addEventListener('keyup', this.arrowDownListener);
		}
	}

	#addEventListeners(): void {
		this.addEventListener(InputFieldEvent.BLURRED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.FOCUSED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.HOVERED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.UN_HOVERED, this.#eventListener as EventListener);
		this.addEventListener(SelectListEvent.SELECTION_CHANGED, this.#listItemChanged as EventListener);
	}

	#blurred(): void {
		this.#removeArrowDownListener();
	}

	#changed(): void {
		this.dataCollection.filter = this.inputField.text;
		if (this.inputField.text.length > 0) {
			if (this.state === 'closed') {
				this.show();
			}
		} else {
			this.hide();
		}
	}

	#eventListener(event: InputFieldEvent): void {
		// prevent the default events to bubble up the DOM. ENTER, HOVERED and UN_HOVERED are listened for
		// purely so they can be stopped here: the InputField is an implementation detail, so a listener
		// above must not hear its events, whether or not we answer one with an event of our own
		event.stopImmediatePropagation();
		if (event.type === InputFieldEvent.FOCUSED) {
			this.#focused();
		} else if (event.type === InputFieldEvent.CHANGED) {
			this.#changed();
		} else if (event.type === InputFieldEvent.BLURRED) {
			this.#blurred();
		}
	}

	#focused(): void {
		this.#addArrowDownListener();
	}

	#focusFirstDropdownRenderer(): void {
		const firstElement = this.listChild.children.item(0);
		if (firstElement && firstElement instanceof DropdownItemRenderer) {
			firstElement.focus();
		}
	}

	#iconAttributeChanged(value: string): void {
		this.icon = value;
	}

	#listItemChanged(event: SelectListEvent<DropdownItem>): void {
		if (event.selectedItem) {
			// the DropdownItem text is optional, so we clear the InputField if there is no text
			this.inputField.text = textOrEmpty(event.selectedItem);
			this.hide();
			this.dispatchEvent(new AutoCompleteEvent(event.selectedItem));
		}
	}

	#placeholderAttributeChanged(value: string): void {
		this.placeholder = value;
	}

	#removeArrowDownListener(): void {
		this.#hasKeyboardListener = false;
		window.removeEventListener('keyup', this.arrowDownListener);
	}

	private arrowDownListener(event: KeyboardEvent): void {
		if (event.code === 'ArrowDown') {
			this.#focusFirstDropdownRenderer();
		}
	}

	private sortFunction(itemA: DropdownItem, itemB: DropdownItem): -1 | 1 {
		if (textOrEmpty(itemA) < textOrEmpty(itemB)) {
			return -1;
		}
		return 1;
	}
}
customElements.define('ac-auto-complete', AutoComplete);

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}

/** The DropdownItem text is optional, so we treat "no text" as an empty string. */
function textOrEmpty(item: DropdownItem): string {
	if (item.text === null) {
		return '';
	}
	return item.text;
}
